// embeeLAB Foto-Reliefbild configurator
// Format and size selection, photo upload (validation + B&W canvas preview
// with a draggable crop frame), R2 upload via presigned URL (kind="logo",
// same pool as the STL uploads), and add-to-cart with line item properties
// (incl. the exact crop area as fractions of the original dimensions, so
// the same crop can be cut from the original later).

const DATA = JSON.parse(document.getElementById('el-fr-data').textContent);
const NETLIFY_BASE = DATA.netlifyBase;

// Aspect ratio (width/height) per format - Hochformat/Querformat are exact
// mirrors of each other (15x20 vs 20x15), so the crop frame always matches
// the real print ratio regardless of which size tier is selected.
const FORMAT_ASPECT = { Quadratisch: 1, Hochformat: 15 / 20, Querformat: 20 / 15 };

const state = {
  variantId: DATA.variants[0] ? parseInt(DATA.variants[0].id, 10) : null,
  format: DATA.variants[0] ? DATA.variants[0].format : 'Quadratisch',
  sizeLabel: DATA.variants[0] ? DATA.variants[0].size : '',
  price: DATA.variants[0] ? DATA.variants[0].price : '',
  originalFilename: '',
  r2Key: '',
  uploaded: false,
  uploading: false,
  naturalW: null,
  naturalH: null,
  crop: null, // { xFrac, yFrac, wFrac, hFrac } relative to the original image size
};

const dropzone = document.getElementById('el-fr-dropzone');
const fileInput = document.getElementById('el-fr-file-input');
const canvasWrap = document.getElementById('el-fr-canvas-wrap');
const canvas = document.getElementById('el-fr-canvas');
const cropFrame = document.getElementById('el-fr-crop-frame');
const cropHint = document.getElementById('el-fr-crop-hint');
const changePhotoBtn = document.getElementById('el-fr-change-photo');
const progressRow = document.getElementById('el-fr-progress-row');
const errorEl = document.getElementById('el-fr-error');
const warningEl = document.getElementById('el-fr-warning');
const noteInput = document.getElementById('el-fr-note');
const formatButtons = document.querySelectorAll('#el-fr-formats .el-fr__format');
const variantButtons = document.querySelectorAll('#el-fr-variants .el-fr__variant');
const sumFormat = document.getElementById('el-fr-sum-format');
const sumSize = document.getElementById('el-fr-sum-size');
const sumPrice = document.getElementById('el-fr-sum-price');
const addBtn = document.getElementById('el-fr-add');
const successEl = document.getElementById('el-fr-success');

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];
const MIN_DIMENSION_WARN = 1000;

function showError(msg) { errorEl.textContent = msg; errorEl.hidden = false; }
function hideError() { errorEl.hidden = true; }
function showWarning(msg) { warningEl.textContent = msg; warningEl.hidden = false; }
function hideWarning() { warningEl.hidden = true; }

function updateSummary() {
  sumFormat.textContent = state.format;
  sumSize.textContent = state.sizeLabel;
  sumPrice.textContent = state.price;
  updateAddButton();
}

function updateAddButton() {
  addBtn.disabled = !(state.variantId && state.uploaded && !state.uploading);
}

// ── Format selection ───────────────────────────────────────────────────
formatButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    if (btn.classList.contains('is-active')) return;
    formatButtons.forEach((b) => b.classList.remove('is-active'));
    btn.classList.add('is-active');
    state.format = btn.dataset.format;

    let firstMatch = null;
    variantButtons.forEach((vb) => {
      const matches = vb.dataset.format === state.format;
      vb.hidden = !matches;
      if (matches && !firstMatch) firstMatch = vb;
    });
    if (firstMatch) {
      variantButtons.forEach((b) => b.classList.remove('is-active'));
      firstMatch.classList.add('is-active');
      state.variantId = parseInt(firstMatch.dataset.variantId, 10);
      state.sizeLabel = firstMatch.dataset.size;
      state.price = firstMatch.dataset.price;
    }

    if (state.naturalW && state.naturalH) {
      resetCropToCenter();
      renderCropFrame();
    }
    updateSummary();
  });
});

// ── Size selection ──────────────────────────────────────────────────────
variantButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    variantButtons.forEach((b) => b.classList.remove('is-active'));
    btn.classList.add('is-active');
    state.variantId = parseInt(btn.dataset.variantId, 10);
    state.sizeLabel = btn.dataset.size;
    state.price = btn.dataset.price;
    updateSummary();
  });
});

// ── Dropzone / file selection ─────────────────────────────────────────────
dropzone.addEventListener('click', () => fileInput.click());
dropzone.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
});
changePhotoBtn.addEventListener('click', () => fileInput.click());

['dragenter', 'dragover'].forEach((evt) => {
  dropzone.addEventListener(evt, (e) => { e.preventDefault(); dropzone.classList.add('is-dragover'); });
});
['dragleave', 'drop'].forEach((evt) => {
  dropzone.addEventListener(evt, (e) => { e.preventDefault(); dropzone.classList.remove('is-dragover'); });
});
dropzone.addEventListener('drop', (e) => {
  const file = e.dataTransfer.files && e.dataTransfer.files[0];
  if (file) handleFile(file);
});
fileInput.addEventListener('change', () => {
  const file = fileInput.files && fileInput.files[0];
  if (file) handleFile(file);
});

function isHeic(file) {
  const name = (file.name || '').toLowerCase();
  const type = (file.type || '').toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif') || type.includes('heic') || type.includes('heif');
}

function handleFile(file) {
  hideError();
  hideWarning();

  if (isHeic(file)) {
    showError('Dieses Format (HEIC) können wir hier nicht direkt anzeigen. Bitte beim Teilen auf dem iPhone "Als JPG" wählen und das Foto erneut hochladen.');
    return;
  }
  if (!ALLOWED_TYPES.includes(file.type)) {
    showError('Bitte ein JPG- oder PNG-Foto hochladen.');
    return;
  }
  if (file.size > MAX_FILE_SIZE) {
    showError('Die Datei ist zu groß (max. 25 MB).');
    return;
  }

  state.originalFilename = file.name;
  state.uploaded = false;
  updateAddButton();

  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      state.naturalW = img.naturalWidth;
      state.naturalH = img.naturalHeight;
      drawGrayscale(img);
      resetCropToCenter();
      renderCropFrame();
      cropFrame.hidden = false;
      cropHint.hidden = false;
      if (Math.min(img.naturalWidth, img.naturalHeight) < MIN_DIMENSION_WARN) {
        showWarning('Dieses Foto ist relativ klein - das Ergebnis könnte dadurch etwas weniger scharf wirken. Kein Problem: Wir prüfen es vor dem Druck und melden uns, falls es nicht reicht.');
      }
      uploadPhoto(file);
    };
    img.onerror = () => showError('Dieses Foto konnte nicht gelesen werden. Bitte ein anderes Foto versuchen.');
    img.src = e.target.result;
  };
  reader.onerror = () => showError('Diese Datei konnte nicht gelesen werden.');
  reader.readAsDataURL(file);
}

function drawGrayscale(img) {
  const MAX_DIM = 900;
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  if (w > h && w > MAX_DIM) { h = Math.round(h * (MAX_DIM / w)); w = MAX_DIM; }
  else if (h >= w && h > MAX_DIM) { w = Math.round(w * (MAX_DIM / h)); h = MAX_DIM; }

  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);

  const imageData = ctx.getImageData(0, 0, w, h);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const gray = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    // Slight contrast boost, hints at the "punch" of the printed relief.
    const contrasted = Math.min(255, Math.max(0, (gray - 128) * 1.15 + 128));
    d[i] = d[i + 1] = d[i + 2] = contrasted;
  }
  ctx.putImageData(imageData, 0, 0);

  dropzone.hidden = true;
  canvasWrap.hidden = false;
  changePhotoBtn.hidden = false;
}

// ── Crop frame (draggable, original-image coordinates) ───────────────────
// Frame size is a "contain" fit into the photo (the largest rectangle in
// the target aspect ratio that fits the photo) and stays fixed while
// dragging - only the position (xFrac/yFrac) is draggable. All values are
// fractions (0-1) of the ORIGINAL image size, independent of the
// (possibly downscaled) preview canvas size.
function resetCropToCenter() {
  const aspect = FORMAT_ASPECT[state.format];
  const w = state.naturalW;
  const h = state.naturalH;
  let cropW;
  let cropH;
  if (w / h > aspect) { cropH = h; cropW = h * aspect; }
  else { cropW = w; cropH = w / aspect; }
  const wFrac = cropW / w;
  const hFrac = cropH / h;
  state.crop = {
    wFrac,
    hFrac,
    xFrac: (1 - wFrac) / 2,
    yFrac: (1 - hFrac) / 2,
  };
}

function renderCropFrame() {
  if (!state.crop || canvasWrap.hidden) return;
  const dispW = canvas.clientWidth;
  const dispH = canvas.clientHeight;
  cropFrame.style.left = `${state.crop.xFrac * dispW}px`;
  cropFrame.style.top = `${state.crop.yFrac * dispH}px`;
  cropFrame.style.width = `${state.crop.wFrac * dispW}px`;
  cropFrame.style.height = `${state.crop.hFrac * dispH}px`;
}

let dragState = null;
cropFrame.addEventListener('pointerdown', (e) => {
  if (!state.crop) return;
  cropFrame.setPointerCapture(e.pointerId);
  dragState = {
    pointerId: e.pointerId,
    startClientX: e.clientX,
    startClientY: e.clientY,
    startXFrac: state.crop.xFrac,
    startYFrac: state.crop.yFrac,
    dispW: canvas.clientWidth,
    dispH: canvas.clientHeight,
  };
});
cropFrame.addEventListener('pointermove', (e) => {
  if (!dragState || e.pointerId !== dragState.pointerId) return;
  const dxFrac = (e.clientX - dragState.startClientX) / dragState.dispW;
  const dyFrac = (e.clientY - dragState.startClientY) / dragState.dispH;
  const maxX = 1 - state.crop.wFrac;
  const maxY = 1 - state.crop.hFrac;
  state.crop.xFrac = Math.min(maxX, Math.max(0, dragState.startXFrac + dxFrac));
  state.crop.yFrac = Math.min(maxY, Math.max(0, dragState.startYFrac + dyFrac));
  renderCropFrame();
});
['pointerup', 'pointercancel'].forEach((evt) => {
  cropFrame.addEventListener(evt, () => { dragState = null; });
});

if (typeof ResizeObserver !== 'undefined') {
  new ResizeObserver(() => renderCropFrame()).observe(canvasWrap);
}

// ── R2 upload (1:1 pattern from el-konfigurator.liquid) ───────────────────
const MAX_RETRIES = 3;

async function uploadFileToR2(file, orderId, bundleRef, onProgress) {
  const urlResp = await fetch(`${NETLIFY_BASE}/get-upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderId, filename: file.name, bundleRef: bundleRef || '', kind: 'logo' }),
  });
  if (!urlResp.ok) {
    const err = await urlResp.json().catch(() => ({}));
    throw new Error(err.error || 'Upload-URL konnte nicht erstellt werden');
  }
  const { uploadUrl, key } = await urlResp.json();

  let lastError;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const xhr = new XMLHttpRequest();
      await new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload fehlgeschlagen (HTTP ${xhr.status})`));
        });
        xhr.addEventListener('error', () => reject(new Error('Netzwerkfehler beim Upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload abgebrochen')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.send(file);
      });
      lastError = null;
      break;
    } catch (e) {
      lastError = e;
      if (attempt < MAX_RETRIES - 1) await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
    }
  }
  if (lastError) throw lastError;
  return key;
}

function makeBundleRef() {
  const rand = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `FOTO-${Date.now().toString(36).toUpperCase()}${rand}`;
}

async function uploadPhoto(file) {
  state.uploading = true;
  updateAddButton();
  progressRow.hidden = false;
  progressRow.textContent = 'Foto wird hochgeladen ...';
  hideError();

  const bundleRef = makeBundleRef();
  try {
    const key = await uploadFileToR2(file, bundleRef, bundleRef, (pct) => {
      progressRow.textContent = `Foto wird hochgeladen ... ${pct}%`;
    });
    state.r2Key = key;
    state.uploaded = true;
    progressRow.hidden = true;
  } catch (e) {
    state.uploaded = false;
    progressRow.hidden = true;
    showError('Upload fehlgeschlagen: ' + e.message + ' - bitte erneut versuchen.');
  } finally {
    state.uploading = false;
    updateAddButton();
  }
}

// ── Add to cart ─────────────────────────────────────────────────────────
function formatCrop(crop) {
  return `x=${crop.xFrac.toFixed(4)} y=${crop.yFrac.toFixed(4)} w=${crop.wFrac.toFixed(4)} h=${crop.hFrac.toFixed(4)}`;
}

addBtn.addEventListener('click', () => {
  if (addBtn.disabled) return;
  const props = {
    'Bild-Referenz': state.r2Key,
    Dateiname: state.originalFilename,
    Format: state.format,
    Größe: state.sizeLabel,
    Bildausschnitt: state.crop ? formatCrop(state.crop) : '',
    Ausführung: 'Schwarz-Weiß, inkl. Rahmen (Aufhängen + Aufstellen)',
  };
  const note = noteInput.value.trim();
  if (note) props.Anmerkung = note;
  const orig = addBtn.textContent;
  addBtn.disabled = true;
  addBtn.textContent = '...';
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: state.variantId, quantity: 1, properties: props }),
  })
    .then((r) => r.json())
    .then((item) => {
      if (item.id) {
        successEl.classList.add('is-shown');
        setTimeout(() => { window.location.href = '/cart'; }, 1200);
      } else {
        addBtn.disabled = false;
        addBtn.textContent = orig;
        alert('Fehler: ' + (item.description || JSON.stringify(item)));
      }
    })
    .catch((err) => {
      addBtn.disabled = false;
      addBtn.textContent = orig;
      alert('Netzwerkfehler: ' + err.message);
    });
});

updateSummary();
